import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Radio, RefreshCw, Play, Sun, Moon, CalendarDays, X, Zap, Check, Clock, TrendingUp } from 'lucide-react';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { PageHead, Card, LoadingState } from './os2ui';
import RachelExecutiveBrief from './RachelExecutiveBrief';
import { AgentHelp } from '../../components/AgentHelp';
import AiWorkspacePage from './AiWorkspacePage';
import AiTeamOsPage from './AiTeamOsPage';

/* AI Executive Team — the executive meeting room, not another dashboard.
   One module, three tabs: Executive Brief (the meeting) · Executive Team (chat & tasks) ·
   Live Operations (event bus timeline, health, product proposals). Real events only. */

const AGENT_META = {
  rachel: { color: '#F77F11' }, stan: { color: '#0ea5e9' }, penny: { color: '#a855f7' },
  sonny: { color: '#10b981' }, eva: { color: '#f43f5e' }, linda: { color: '#eab308' },
  exec_team: { color: '#64748b' },
};
const EXEC_TITLES = {
  rachel: { en: 'Chief of Staff', es: 'Jefa de Gabinete' },
  stan: { en: 'Sales Director', es: 'Director de Ventas' },
  penny: { en: 'Marketing Director', es: 'Directora de Marketing' },
  sonny: { en: 'Distribution Director', es: 'Director de Distribución' },
  eva: { en: 'Operations Director', es: 'Directora de Operaciones' },
  linda: { en: 'Finance Director', es: 'Directora Financiera' },
};
const avatarSrc = (a) => `/avatars/${a}.png`;
const agentOf = (src) => (AGENT_META[src] ? src : 'rachel');
const hhmm = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const ago = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

function ConfPill({ pct, isEs }) {
  const tone = pct >= 85 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : pct >= 70 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-500 border-gray-200';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${tone}`}>
      {isEs ? 'Confianza' : 'Confidence'} {pct}%
    </span>
  );
}

/* -------- Daily executive summary strip -------- */
function DailySummary({ s, isEs }) {
  if (!s) return null;
  const items = [
    [s.opportunities, isEs ? 'oportunidades detectadas' : 'opportunities detected'],
    [s.tasks_auto_assigned, isEs ? 'tareas auto-asignadas' : 'tasks auto-assigned'],
    [s.drafts_today, isEs ? 'borradores/artículos hoy' : 'drafts/articles today'],
    [s.proposals_open, isEs ? 'propuestas de producto abiertas' : 'open product proposals'],
    [s.events_24h, isEs ? 'eventos procesados (24h)' : 'events processed (24h)'],
  ].filter(([n]) => n > 0);
  return (
    <Card className="p-4" data-testid="exec-daily-summary">
      <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-gray-400">
        {isEs ? 'Hoy tu Executive Team ya ha' : 'Today your Executive Team has already'}
      </p>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5">
        {items.map(([n, t]) => (
          <span key={t} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-700">
            <Check className="w-3.5 h-3.5 text-emerald-600" /> {n} {t}
          </span>
        ))}
        {typeof s.hours_saved === 'number' && s.hours_saved > 0 && (
          <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#d8651a]" data-testid="exec-hours-saved">
            <Clock className="w-3.5 h-3.5" /> ≈ {s.hours_saved}h {isEs ? 'ahorradas' : 'saved'}
          </span>
        )}
      </div>
    </Card>
  );
}

/* -------- Executive Recommendations -------- */
function RecommendationCard({ r, isEs, onDismiss }) {
  const name = r.agent.charAt(0).toUpperCase() + r.agent.slice(1);
  const dismissed = r.status === 'dismissed';
  return (
    <Card className={`p-4 ${dismissed ? 'opacity-45' : ''}`} data-testid={`exec-reco-${r.rule}`}>
      <div className="flex items-start gap-3">
        <img src={avatarSrc(r.agent)} alt={name} className="w-10 h-10 rounded-full object-cover shrink-0"
          onError={(e) => { e.target.style.visibility = 'hidden'; }} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[12px] font-bold text-gray-900">{name}</p>
            <p className="text-[10.5px] font-bold flex items-center gap-1" style={{ color: AGENT_META[r.agent]?.color }}>
              {EXEC_TITLES[r.agent] ? (isEs ? EXEC_TITLES[r.agent].es : EXEC_TITLES[r.agent].en) : ''} <AgentHelp agent={r.agent} isEs={isEs} />
            </p>
            <ConfPill pct={r.confidence_pct} isEs={isEs} />
            <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${dismissed ? 'text-gray-400 border-gray-200 bg-gray-50' : 'text-sky-700 border-sky-200 bg-sky-50'}`}>
              {dismissed ? (isEs ? 'cancelada' : 'cancelled') : (isEs ? 'auto-asignada' : 'auto-assigned')}
            </span>
          </div>
          <p className="mt-1.5 text-[14px] font-semibold text-gray-900 leading-snug">{isEs ? r.title_es : r.title_en}</p>
          <p className="mt-1 text-[12px] text-gray-500 leading-snug">
            <b className="text-gray-600">{isEs ? 'Por qué: ' : 'Why: '}</b>{isEs ? r.why_es : r.why_en}
          </p>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[11.5px] text-gray-500">
            {r.impact_usd != null && (
              <span className="inline-flex items-center gap-1 font-bold text-emerald-700">
                <TrendingUp className="w-3.5 h-3.5" /> ${Number(r.impact_usd).toLocaleString()}
                <span className="font-normal text-gray-400">· {isEs ? r.impact_note_es : r.impact_note_en}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-gray-300" /> ≈ {r.eta_min} min</span>
            <span><b className="text-gray-600">{isEs ? 'Resultado esperado: ' : 'Expected outcome: '}</b>{isEs ? r.outcome_es : r.outcome_en}</span>
          </div>
        </div>
        {!dismissed && (
          <button onClick={() => onDismiss(r.id)} data-testid={`exec-reco-dismiss-${r.rule}`}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
            aria-label={isEs ? 'Cancelar iniciativa' : 'Cancel initiative'}>
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </Card>
  );
}

function BriefTab({ isEs }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    authFetch('/admin/exec/initiatives').then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const dismiss = async (id) => {
    await authFetch(`/admin/exec/initiatives/${id}/dismiss`, { method: 'POST' }).catch(() => {});
    load();
  };
  return (
    <div className="space-y-4" data-testid="exec-tab-brief-content">
      <DailySummary s={d?.summary} isEs={isEs} />
      <RachelExecutiveBrief isEs={isEs} />
      <div data-testid="exec-recommendations">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-[16px] font-semibold tracking-tight text-gray-900">Executive Recommendations</h2>
            <p className="text-[11.5px] text-gray-400">
              {isEs ? 'Lo que merece tu atención hoy — preparado y auto-asignado; nada de cara al cliente se ejecuta sin ti.'
                : 'What deserves your attention today — prepared and auto-assigned; nothing customer-facing runs without you.'}
            </p>
          </div>
        </div>
        <div className="mt-3 space-y-3">
          {(d?.initiatives || []).map((r) => <RecommendationCard key={r.id} r={r} isEs={isEs} onDismiss={dismiss} />)}
          {d && !d.initiatives?.length && (
            <p className="text-[12px] text-gray-400">{isEs ? 'El equipo genera sus movimientos del día en el próximo ciclo.' : 'The team generates today\u2019s moves on the next cycle.'}</p>
          )}
          {!d && <LoadingState />}
        </div>
      </div>
    </div>
  );
}

/* -------- Live Operations -------- */
function Timeline({ feed, isEs }) {
  return (
    <Card className="p-4" data-testid="exec-timeline">
      <h2 className="text-[13.5px] font-semibold tracking-tight flex items-center gap-2">
        <Radio className="w-4 h-4 text-[#F77F11] animate-pulse" /> {isEs ? 'Timeline ejecutivo' : 'Executive timeline'}
        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{isEs ? 'en vivo' : 'live'}</span>
      </h2>
      <div className="mt-3 max-h-[520px] overflow-y-auto pr-1">
        {feed.map((f, i) => {
          const a = agentOf((f.source || '').toLowerCase());
          const title = EXEC_TITLES[a] ? (isEs ? EXEC_TITLES[a].es : EXEC_TITLES[a].en) : f.source;
          return (
            <div key={i} className="relative flex items-start gap-3 pb-3 last:pb-0">
              {i < feed.length - 1 && <span className="absolute left-[9px] top-6 bottom-0 w-px bg-gray-100" aria-hidden="true" />}
              <span className="mt-1 w-[19px] h-[19px] rounded-full border-2 border-white shadow shrink-0 overflow-hidden" style={{ background: AGENT_META[a]?.color || '#F77F11' }}>
                <img src={avatarSrc(a)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-gray-400 font-bold tabular-nums">{hhmm(f.created_at)} <span className="font-semibold text-gray-500 capitalize">· {f.source}</span> <span className="text-gray-300 normal-case">{title !== f.source ? `· ${title}` : ''}</span></p>
                <p className="text-[12px] text-gray-700 font-semibold">{String(f.type || '').replace(/_/g, ' ').toLowerCase()}</p>
                {Object.keys(f.payload || {}).length > 0 && (
                  <p className="text-[10.5px] text-gray-400 truncate">
                    {Object.entries(f.payload).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                  </p>
                )}
              </div>
            </div>
          );
        })}
        {!feed.length && <p className="text-[12px] text-gray-400">{isEs ? 'Sin eventos aún.' : 'No events yet.'}</p>}
      </div>
    </Card>
  );
}

function OpsTab({ isEs }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    authFetch('/admin/exec/autonomy').then((r) => (r.ok ? r.json() : Promise.reject(r.status))).then(setD).catch((e) => setErr(String(e)));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const dismiss = async (id) => {
    await authFetch(`/admin/exec/proposals/${id}/dismiss`, { method: 'POST' }).catch(() => {});
    load();
  };
  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;
  return (
    <div className="space-y-4" data-testid="exec-tab-ops">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="autonomy-cycle">
        {[[Sun, isEs ? 'Mañana' : 'Morning', isEs ? 'Executive Brief de Rachel' : 'Rachel Executive Brief'],
          [Zap, isEs ? 'Cada hora' : 'Hourly', isEs ? 'El equipo observa el negocio' : 'The team observes the business'],
          [Moon, isEs ? 'Tarde' : 'Evening', isEs ? 'Resumen de progreso' : 'Progress summary'],
          [CalendarDays, isEs ? 'Lunes' : 'Monday', isEs ? 'Reporte ejecutivo semanal' : 'Executive weekly report']].map(([Icon, k, v]) => (
            <div key={k} className="rounded-2xl border border-gray-200 bg-white p-3">
              <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-400"><Icon className="w-3.5 h-3.5 text-[#F77F11]" /> {k}</p>
              <p className="mt-1 text-[11.5px] font-semibold text-gray-700 leading-snug">{v}</p>
            </div>
          ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2 space-y-4">
          <Timeline feed={d.feed || []} isEs={isEs} />

          <Card className="p-4" data-testid="autonomy-proposals">
            <h2 className="text-[13.5px] font-semibold tracking-tight">{isEs ? 'Propuestas de mejora del producto' : 'Product Improvement Proposals'}</h2>
            <p className="text-[10.5px] text-gray-400">{isEs ? 'Fricción detectada en el uso real de KITXENS — nada se cambia sin tu aprobación.' : 'Friction detected in real KITXENS usage — nothing changes without your approval.'}</p>
            <div className="mt-2 space-y-2">
              {d.proposals.map((p) => (
                <div key={p.id} className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 flex items-start gap-2" data-testid={`autonomy-proposal-${p.rule_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-amber-600">{p.area}</p>
                    <p className="mt-0.5 text-[12px] text-gray-700 leading-snug">{isEs ? p.proposal_es : p.proposal_en}</p>
                    <p className="mt-1 text-[10.5px] text-gray-400 font-mono">{Object.entries(p.evidence || {}).map(([k, v]) => `${k}=${v}`).join(' · ')}</p>
                  </div>
                  <button onClick={() => dismiss(p.id)} data-testid={`autonomy-proposal-dismiss-${p.rule_id}`}
                    className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-amber-400 hover:text-amber-700 hover:bg-amber-100 transition-colors" aria-label={isEs ? 'Descartar' : 'Dismiss'}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {!d.proposals.length && <p className="text-[12px] text-gray-400">{isEs ? 'Sin fricción significativa detectada ahora mismo.' : 'No significant friction detected right now.'}</p>}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3" data-testid="autonomy-observations">
            {d.observations.map((o) => (
              <Card key={o.agent} className="p-3.5" data-testid={`autonomy-obs-${o.agent}`}>
                <div className="flex items-center gap-2">
                  <img src={avatarSrc(o.agent)} alt={o.agent} className="w-8 h-8 rounded-full object-cover" />
                  <div>
                    <p className="text-[12.5px] font-bold text-gray-900 capitalize">{o.agent}
                      <span className="ml-1.5 text-[9.5px] uppercase tracking-wider font-bold text-gray-300">{EXEC_TITLES[o.agent] ? (isEs ? EXEC_TITLES[o.agent].es : EXEC_TITLES[o.agent].en) : ''}</span>
                    </p>
                    <p className="text-[9.5px] uppercase tracking-wider font-bold text-gray-300">{ago(o.at)} · {isEs ? 'observando' : 'observing'}</p>
                  </div>
                  {o.significant && <span className="ml-auto w-2 h-2 rounded-full animate-pulse" style={{ background: AGENT_META[o.agent]?.color || '#F77F11' }} />}
                </div>
                <p className="mt-2 text-[12px] text-gray-600 leading-snug">{isEs ? o.headline_es : o.headline_en}</p>
              </Card>
            ))}
          </div>

          <Card className="p-4" data-testid="autonomy-providers">
            <h2 className="text-[13.5px] font-semibold tracking-tight">{isEs ? 'Salud del sistema' : 'System health'}</h2>
            <p className="text-[10.5px] text-gray-400">discover · health · verify · publish · report</p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(d.providers || []).map((p) => {
                const st = String(p.health?.status || 'unknown');
                const good = ['ok', 'operational', 'connected'].includes(st);
                return (
                  <div key={p.provider} className="rounded-xl border border-gray-200 p-3" data-testid={`autonomy-provider-${p.provider}`}>
                    <p className="text-[12px] font-bold text-gray-900">{p.provider === 'dlvrit' ? 'dlvr.it' : p.provider}</p>
                    <p className={`mt-0.5 text-[10.5px] font-bold uppercase tracking-wide ${good ? 'text-emerald-600' : 'text-gray-400'}`}>{st}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      <AiTeamOsPage embedded />
    </div>
  );
}

const TAB_MAP = { brief: 'brief', team: 'team', chat: 'team', ops: 'ops' };

export default function AutonomyPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [params, setParams] = useSearchParams();
  const tab = TAB_MAP[params.get('tab')] || 'brief';
  const setTab = (t) => { const p = new URLSearchParams(params); p.set('tab', t); setParams(p, { replace: true }); };
  const [running, setRunning] = useState(false);
  const runNow = async () => {
    setRunning(true);
    try { await authFetch('/admin/exec/run', { method: 'POST' }); } finally { setRunning(false); }
  };

  const TABS = [
    ['brief', 'Executive Brief', 'Executive Brief'],
    ['team', 'Executive Team', 'Executive Team'],
    ['ops', 'Live Operations', 'Live Operations'],
  ];

  return (
    <div data-testid="os2-autonomy">
      <PageHead
        title="AI Executive Team"
        question={isEs ? 'Tu equipo ejecutivo AI vive aquí: habla con cualquiera, asigna tareas y mira su actividad en vivo.' : 'Your AI executive team lives here: talk to anyone, assign tasks and watch their live activity.'}
        module="Beta 4"
        right={
          <button onClick={runNow} disabled={running} data-testid="autonomy-run-now"
            className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 hover:bg-[#F77F11] text-white text-[12.5px] font-semibold px-4 py-2 transition-colors disabled:opacity-50">
            {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {isEs ? 'Ejecutar ciclo ahora' : 'Run cycle now'}
          </button>
        }
      />

      <div className="mb-4 inline-flex rounded-full border border-gray-200 bg-white p-1" data-testid="exec-tabs">
        {TABS.map(([k, en, es]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`exec-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-[12px] font-bold transition-colors ${tab === k ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            {isEs ? es : en}
          </button>
        ))}
      </div>

      {tab === 'brief' && <BriefTab isEs={isEs} />}
      {tab === 'team' && <AiWorkspacePage embedded />}
      {tab === 'ops' && <OpsTab isEs={isEs} />}

      <p className="mt-4 text-[10.5px] text-gray-300">
        {isEs ? 'Beta 4 · El Event Bus orquesta. Rachel coordina. La preparación es automática; la ejecución de cara al cliente sigue siendo humana.' : 'Beta 4 · The Event Bus orchestrates. Rachel coordinates. Preparation is automatic; customer-facing execution stays human.'}
      </p>
    </div>
  );
}
